<script>
import { onMount, tick } from "svelte";
import { toast } from "@zerodevx/svelte-toast";
import { Grid, HeaderMenu, WillowDark } from "@svar-ui/svelte-grid";
import debounce from "just-debounce-it";
import * as m from "@app/paraglide/messages.js";
import tcLib from "@cclib/lib/timecode.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import {
    eventGroupState,
    isSelectedEventGroupLocked,
} from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import {
    editorState,
    saveEditorSettings,
} from "@app/store/editorStore.svelte.js";
import { commitHistory } from "@app/store/historyStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import { getMetadataKindForTableColumn } from "@app/store/permissionStore.svelte.js";
import {
    getEventMetrics,
    getEventErrors,
} from "@app/lib/eventValidation.js";
import {
    applyEventClick,
    getSelectionAnchor,
    setSelectionAnchor,
} from "@app/lib/eventSelection.js";
import {
    buildEventTableColumns,
    getHeaderMenuColumns,
} from "./table/eventTableColumns.js";

/**
 * @typedef {{ event: any, originalIndex: number }} FilteredEvent
 * @typedef {{ filteredEvents?: FilteredEvent[], listHeight?: number }} Props
 */

/** @type {Props} */
let { filteredEvents = [], listHeight = 400 } = $props();

let api = $state();
/** Guard to prevent selection feedback loops with the grid API (not reactive). */
let syncingSelection = false;
/** Wrapper element used to correct SVAR's estimated scroll position. */
let tableWrapEl = $state();

let isMultiview = $derived(
    eventGroupState[projectState.selected]?.type === "multiview",
);

const TRACK_LETTERS = ["A", "B", "C", "D"];

/** Track options: numbered tracks for multiview groups, letters (A-D) otherwise */
let trackOptions = $derived.by(() => {
    if (!isMultiview) {
        return [
            { id: "none", label: m.event_track_none() },
            ...TRACK_LETTERS.map((letter) => ({
                id: letter,
                label: `${m.editor_table_column_track()} ${letter}`,
            })),
        ];
    }
    const eventGroup = eventGroupState[projectState.selected];
    const tracks =
        Array.isArray(eventGroup.tracks) && eventGroup.tracks.length >= 2
            ? eventGroup.tracks
            : Array.from(
                  {
                      length: Math.max(
                          2,
                          ...eventGroup.events.map((event) => event.track || 1),
                      ),
                  },
                  () => ({}),
              );
    return tracks.map((track, i) => ({
        id: String(i + 1),
        label: track.name || `${m.editor_table_column_track()} ${i + 1}`,
    }));
});

let headerMenuColumns = $derived(getHeaderMenuColumns());

let columns = $derived(
    buildEventTableColumns(
        editorState.tableColumnVisibility,
        editorState.tableColumnOrder,
        !!eventGroupState[projectState.selected]?.allowOriginalEdit,
        trackOptions,
    ),
);

/**
 * @param {number | false | null | undefined} sec
 */
function formatTc(sec) {
    if (sec === false || sec == null || Number.isNaN(sec)) {
        return "--:--:--:--";
    }
    try {
        return tcLib.secToTc(
            sec + projectState.incode,
            projectState.frameRate,
            projectState.dropFrame,
        );
    } catch {
        return "--:--:--:--";
    }
}

/**
 * @param {number | false | null | undefined} start
 * @param {number | false | null | undefined} end
 */
function formatDuration(start, end) {
    if (
        start === false ||
        end === false ||
        start == null ||
        end == null ||
        Number.isNaN(start) ||
        Number.isNaN(end)
    ) {
        return "--:--";
    }
    try {
        return tcLib.secToTcClock(
            Math.max(0, end - start),
            projectState.frameRate,
            projectState.dropFrame,
        );
    } catch {
        return "--:--";
    }
}

let rows = $derived.by(() => {
    return filteredEvents.map(({ event, originalIndex }) => {
        const metrics = getEventMetrics(event);
        const speakers = Array.isArray(event.speakers)
            ? event.speakers.map((s) => s?.name || s).filter(Boolean).join(", ")
            : "";
        const recipients = Array.isArray(event.recipients)
            ? event.recipients.map((r) => r?.name || r).filter(Boolean).join(", ")
            : "";
        const tags = Array.isArray(event.tags) ? event.tags : [];

        let approved = "undefined";
        if (event.approved === true) approved = "true";
        else if (event.approved === false) approved = "false";

        const eventGroup = eventGroupState[projectState.selected];
        const eventErrors = getEventErrors(event, eventGroup, originalIndex);
        const errorMsg = eventErrors.map((e) => `• ${e.msg}`).join("\n");

        return {
            id: event.id,
            originalIndex,
            number: String(originalIndex + 1).padStart(4, "0"),
            errorMsg,
            start: formatTc(event.start),
            end: formatTc(event.end),
            duration: formatDuration(event.start, event.end),
            cps:
                metrics.cps && Number.isFinite(metrics.cps)
                    ? Math.round(metrics.cps * 10) / 10
                    : "",
            wpm:
                metrics.wpm && Number.isFinite(metrics.wpm)
                    ? Math.round(metrics.wpm)
                    : "",
            alignment: event.alignment || "left",
            xPos: event.xPos ?? "center",
            yPos:
                typeof event.yPos === "number"
                    ? event.yPos
                    : (event.yPos ?? "end"),
            style: event.style || "Pop-On",
            text: convertToPlainText(event.text || ""),
            original: convertToPlainText(event.original || ""),
            speakers,
            forced: event.forced ? "true" : "false",
            track: isMultiview
                ? String(event.track || 1)
                : TRACK_LETTERS.includes(event.track)
                  ? event.track
                  : "none",
            approved,
            notes: event.notes || "",
            reply: event.reply || "",
            notesCheck: event.notesCheck ? "true" : "false",
            replyCheck: event.replyCheck ? "true" : "false",
            voice: event.voice?.name || "",
            recipients,
            tags,
            _hasError: eventErrors.length > 0,
        };
    });
});

const WRAP_COLUMN_IDS = new Set([
    "text",
    "original",
    "notes",
    "reply",
    "speakers",
    "voice",
    "recipients",
]);

/**
 * @param {any} row
 */
function rowStyle(row) {
    const classes = [];
    if (row?._hasError) {
        classes.push("event-table-error");
    } else if (row?.forced === "true") {
        classes.push("event-table-forced");
    }
    const firstSelected =
        eventGroupState[projectState.selected]?.selected?.[0];
    if (row?.originalIndex === firstSelected) {
        classes.push("event-table-first-selected");
    }
    return classes.join(" ");
}

/**
 * @param {any} row
 * @param {{ id?: string }} column
 */
function cellStyle(row, column) {
    if (!WRAP_COLUMN_IDS.has(column?.id)) return "";
    const firstSelected =
        eventGroupState[projectState.selected]?.selected?.[0];
    if (row?.originalIndex === firstSelected) {
        return "event-table-wrap-cell";
    }
    return "";
}

/**
 * Sync grid selection into eventGroupState
 * @param {{ id: any, toggle?: boolean, range?: boolean, mode?: boolean }} ev
 */
function handleSelectRow(ev) {
    if (syncingSelection) return;
    const egIdx = projectState.selected;
    const eventGroup = eventGroupState[egIdx];
    if (egIdx == null || !eventGroup) return;

    // After select-row, read current selectedRows from grid state
    queueMicrotask(() => {
        if (!api) return;
        const selectedIds = api.getState()?.selectedRows || [];
        const indexMap = new Map(
            filteredEvents.map(({ event, originalIndex }) => [
                event.id,
                originalIndex,
            ]),
        );
        const nextSelected = selectedIds
            .map((id) => indexMap.get(id))
            .filter((idx) => idx != null);

        syncingSelection = true;
        eventGroup.selected = nextSelected;
        syncingSelection = false;

        if (!ev?.range) {
            const clickedIndex = indexMap.get(ev?.id);
            if (clickedIndex != null) {
                setSelectionAnchor(eventGroup.id, clickedIndex);
            }
        }

        // Seek video on single selection (matches list view)
        if (
            nextSelected.length === 1 &&
            lockState.video &&
            typeof player !== "undefined" &&
            player
        ) {
            const start = eventGroup.events[nextSelected[0]]?.start;
            if (start !== false && start != null && !Number.isNaN(start)) {
                player.currentTime = start;
            }
        }
    });
}

/**
 * Apply cell edits from the grid back to eventGroupState
 * @param {{ id: any, column: any, value: any }} ev
 */
function handleUpdateCell(ev) {
    const { id, column, value } = ev || {};
    if (id == null || column == null) return;

    if (isSelectedEventGroupLocked() && !getMetadataKindForTableColumn(column)) {
        return;
    }

    const egIdx = projectState.selected;
    const row = rows.find((r) => r.id === id);
    if (!row || egIdx == null) return;

    const originalIndex = row.originalIndex;

    if (column === "style") {
        commitHistory({
            name: "update style",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    const evDraft = draft[egIdx].events[originalIndex];
                    if (!evDraft) return;
                    evDraft.style = value;
                },
            },
        });
        return;
    }

    if (column === "forced") {
        commitHistory({
            name: "update forced",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    const evDraft = draft[egIdx].events[originalIndex];
                    if (!evDraft) return;
                    evDraft.forced = value === "true" || value === true;
                },
            },
        });
        return;
    }

    if (column === "track") {
        commitHistory({
            name: "update event track",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    const evDraft = draft[egIdx].events[originalIndex];
                    if (!evDraft) return;
                    evDraft.track = isMultiview
                        ? parseInt(value, 10) || 1
                        : TRACK_LETTERS.includes(value)
                          ? value
                          : false;
                },
            },
        });
        return;
    }

    if (column === "approved") {
        commitHistory({
            name: "update approval",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    const evDraft = draft[egIdx].events[originalIndex];
                    if (!evDraft) return;
                    if (value === "true" || value === true) {
                        evDraft.approved = true;
                    } else if (value === "false" || value === false) {
                        evDraft.approved = false;
                    } else {
                        evDraft.approved = undefined;
                    }
                },
            },
        });
        return;
    }

    if (column === "notes") {
        commitHistory({
            name: "update notes",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    const evDraft = draft[egIdx].events[originalIndex];
                    if (!evDraft) return;
                    evDraft.notes = value ?? "";
                },
            },
        });
        return;
    }

    if (column === "reply") {
        commitHistory({
            name: "update reply",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    const evDraft = draft[egIdx].events[originalIndex];
                    if (!evDraft) return;
                    evDraft.reply = value ?? "";
                },
            },
        });
    }
}

/**
 * Persist column visibility when HeaderMenu toggles columns
 * @param {{ id: any, mode?: boolean }} ev
 */
function handleHideColumn(ev) {
    const { id, mode } = ev || {};
    if (id == null) return;
    // mode true = hide
    editorState.tableColumnVisibility = {
        ...editorState.tableColumnVisibility,
        [id]: !mode,
    };
    saveEditorSettings();
}

/**
 * Keep grid selection in sync when selection changes externally
 */
$effect(() => {
    if (!api || syncingSelection) return;
    const selected = eventGroupState[projectState.selected]?.selected || [];
    const idByIndex = new Map(
        filteredEvents.map(({ event, originalIndex }) => [
            originalIndex,
            event.id,
        ]),
    );
    const desiredIds = selected
        .map((idx) => idByIndex.get(idx))
        .filter((id) => id != null);

    const currentIds = api.getState()?.selectedRows || [];
    const same =
        desiredIds.length === currentIds.length &&
        desiredIds.every((id) => currentIds.includes(id));
    if (same) return;

    syncingSelection = true;
    // Clear then re-select
    currentIds.forEach((id) => {
        api.exec("select-row", { id, mode: false });
    });
    desiredIds.forEach((id, i) => {
        api.exec("select-row", {
            id,
            mode: true,
            toggle: i > 0,
        });
    });
    syncingSelection = false;
});

/** Cancels in-flight scroll-correction loops when a newer jump starts. */
let scrollGeneration = 0;

/**
 * Wait for SVAR's layout cycle after a scrollTop change:
 * onscroll -> "scroll-to" store update -> re-render -> adjustHeight (tick).
 */
async function waitForTableLayout() {
    await tick();
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await tick();
}

/**
 * SVAR's "scroll" action estimates the offset as rowHeight (32px) * index,
 * but autoRowHeight rows are taller, so long jumps land short of the target.
 * Converge on the real position: while the target row isn't rendered, step
 * by (rows remaining * average rendered row height); once rendered, align it
 * into view with getBoundingClientRect and keep correcting until the layout
 * settles (adjustHeight can shift rows after they are measured).
 *
 * Stop once scrollTop is clamped at max: an expanded last row can be taller
 * than the remaining viewport, and retrying causes last vs second-to-last
 * stutter.
 * @param {any} id
 * @param {number} targetIndex index of the target row in filteredEvents
 */
async function correctTableScroll(id, targetIndex) {
    const generation = ++scrollGeneration;
    const scroller = tableWrapEl?.querySelector(".wx-scroll");
    if (!scroller) return;

    // SVAR's setID prefixes string ids with ":" in data-id.
    const toRowKey = (/** @type {any} */ value) =>
        typeof value === "string" ? ":" + value : String(value);
    const targetKey = toRowKey(id);
    const indexByKey = new Map(
        filteredEvents.map(({ event }, i) => [toRowKey(event.id), i]),
    );

    const MAX_ATTEMPTS = 40;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        await waitForTableLayout();
        if (generation !== scrollGeneration || !tableWrapEl) return;

        const renderedRows = Array.from(
            scroller.querySelectorAll(".wx-row[data-id]"),
        );
        if (!renderedRows.length) continue;

        const target = renderedRows.find(
            (row) => row.getAttribute("data-id") === targetKey,
        );

        if (target) {
            const headerOffset =
                parseFloat(getComputedStyle(scroller).scrollPaddingTop) || 34;
            const scrollerRect = scroller.getBoundingClientRect();
            const rowRect = target.getBoundingClientRect();
            const viewTop = scrollerRect.top + headerOffset;
            const viewBottom = scrollerRect.top + scroller.clientHeight;
            const topDelta = rowRect.top - viewTop;
            const bottomDelta = rowRect.bottom - viewBottom;

            if (topDelta >= -1 && bottomDelta <= 1) return; // fully in view

            const previousTop = scroller.scrollTop;
            if (topDelta < -1) {
                // Row is above the viewport: align its top under the header
                scroller.scrollTop += topDelta;
            } else {
                // Row is below: bring its bottom to the viewport edge, but
                // never push its top above the header (tall rows)
                scroller.scrollTop += Math.min(bottomDelta, topDelta);
            }
            // Clamped at max: last expanded row cannot fully fit. Stop so
            // adjustHeight cannot oscillate last vs second-to-last.
            if (scroller.scrollTop === previousTop) return;
            // Loop again: adjustHeight may shift rows after measuring
            continue;
        }

        // Target not rendered yet — estimate remaining distance from the
        // rendered rows' real heights. Do NOT re-exec SVAR "scroll": it
        // would reset back to the 32px estimate.
        const indexes = renderedRows
            .map((row) => indexByKey.get(row.getAttribute("data-id")))
            .filter((i) => i != null);
        if (!indexes.length) continue;

        const firstIndex = Math.min(...indexes);
        const lastIndex = Math.max(...indexes);
        const avgHeight =
            renderedRows.reduce((sum, row) => sum + row.offsetHeight, 0) /
                renderedRows.length || 40;

        const previousTop = scroller.scrollTop;
        if (targetIndex > lastIndex) {
            scroller.scrollTop += (targetIndex - lastIndex) * avgHeight;
        } else if (targetIndex < firstIndex) {
            scroller.scrollTop -= (firstIndex - targetIndex) * avgHeight;
        }
        // Unchanged scrollTop is not treated as done: scrollHeight grows as
        // rows get measured, so simply retry on the next frame — unless we
        // are already clamped at the bottom and cannot bring the target in.
        if (
            scroller.scrollTop === previousTop &&
            targetIndex > lastIndex &&
            previousTop >=
                scroller.scrollHeight - scroller.clientHeight - 1
        ) {
            return;
        }
    }
}

onMount(() => {
    // Scroll only — selection is already updated by caption lock / other emitters.
    // Re-selecting here would fire handleSelectRow and seek again when video lock is on
    // (feedback loop with caption lock). List view scrolls without re-selecting.
    const handleScrollToEvent = debounce((event) => {
        const originalIndex = parseInt(event.detail?.index, 10);
        if (Number.isNaN(originalIndex) || !api) return;
        const filteredIndex = filteredEvents.findIndex(
            (item) => item.originalIndex === originalIndex,
        );
        if (filteredIndex !== -1) {
            const match = filteredEvents[filteredIndex];
            api.exec("scroll", { row: match.event.id });
            correctTableScroll(match.event.id, filteredIndex);
        }
    }, 50);

    window.addEventListener("scrollToVirtualListItem", handleScrollToEvent);
    return () => {
        window.removeEventListener(
            "scrollToVirtualListItem",
            handleScrollToEvent,
        );
    };
});

/**
 * @param {any} gridApi
 */
function init(gridApi) {
    api = gridApi;
    gridApi.intercept("select-row", (ev) => {
        if (syncingSelection || !ev?.range) return true;
        const eventGroup = eventGroupState[projectState.selected];
        if (!eventGroup) return true;

        const clickedIndex = filteredEvents.find(
            ({ event }) => event.id === ev.id,
        )?.originalIndex;
        if (clickedIndex == null) return true;

        const { selected, anchor } = applyEventClick({
            eventIndex: clickedIndex,
            modifiers: { shiftKey: true },
            currentSelected: eventGroup.selected,
            filteredIndices: filteredEvents.map((item) => item.originalIndex),
            anchor: getSelectionAnchor(eventGroup.id, eventGroup.selected?.[0]),
        });
        eventGroup.selected = selected;
        setSelectionAnchor(eventGroup.id, anchor);
        return false;
    });
    gridApi.on("select-row", handleSelectRow);
    gridApi.intercept("update-cell", (ev) => {
        if (isSelectedEventGroupLocked() && !getMetadataKindForTableColumn(ev?.column)) {
            toast.push(m.event_group_locked_toast(), {
                classes: ["toast-warning"],
            });
            return false;
        }
        return true;
    });
    gridApi.on("update-cell", handleUpdateCell);
    gridApi.on("hide-column", handleHideColumn);
}
</script>

<div
    bind:this={tableWrapEl}
    class="events-table-wrap"
    style="height: {listHeight}px;"
    role="region"
    aria-label={m.editor_table_view_aria_label()}>
    <WillowDark>
        <HeaderMenu api={api} columns={headerMenuColumns}>
            <Grid
                data={rows}
                {columns}
                multiselect={true}
                select={true}
                autoRowHeight={true}
                sizes={{ rowHeight: 32, headerHeight: 34 }}
                {rowStyle}
                {cellStyle}
                {init}
                bind:this={api}
            />
        </HeaderMenu>
    </WillowDark>
</div>

<style>
.events-table-wrap {
    width: 100%;
    overflow: hidden;
    min-height: 0;
    flex: 1;
}
/* Slack so an expanded last row can scroll fully into view.
   content-box: Bootstrap border-box would eat this padding from SVAR's
   explicit fullHeight, shrinking the virtualizer instead of extending it. */
.events-table-wrap :global(.wx-body) {
    box-sizing: content-box;
    padding-bottom: 96px;
}
/* Override WillowDark select tokens so all selected rows are yellow */
.events-table-wrap :global(.wx-willow-dark-theme) {
    --wx-table-select-background: rgba(255, 193, 7, 0.25);
    --wx-table-select-border: inset 0 0 0 1px #ffc107;
}
.events-table-wrap :global(.wx-grid) {
    height: 100%;
}
.events-table-wrap :global(.event-table-error) {
    background: rgba(220, 53, 69, 0.12);
}
/* Forced subtitle rows - matches the bg-info bg-opacity-10 tint in list view */
.events-table-wrap :global(.event-table-forced) {
    background: rgba(13, 202, 240, 0.1);
}
.events-table-wrap :global(.wx-row.wx-selected) {
    background: rgba(255, 193, 7, 0.25);
}
/* Undo SVAR wrapping on every autoheight row so unselected rows stay ~32px */
.events-table-wrap :global(.wx-row.wx-autoheight .wx-cell) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    word-break: normal;
}
/* Restore wrap only on the first selected Event */
.events-table-wrap :global(.wx-row.event-table-first-selected .wx-cell.event-table-wrap-cell),
.events-table-wrap :global(.wx-row.wx-autoheight .wx-cell:has(.text-cell.is-expanded)),
.events-table-wrap :global(.wx-row.wx-autoheight .wx-cell:has(.quill-text-idle.is-expanded)) {
    white-space: normal;
    overflow-wrap: anywhere;
    text-overflow: clip;
    word-break: break-word;
    vertical-align: top;
}
/* Allow inline Quill to paint over adjacent rows only while editing */
.events-table-wrap :global(.wx-row:has(.quill-text-cell.is-editing)),
.events-table-wrap :global(.wx-row:has(.quill-text-cell.is-editing) .wx-cell),
.events-table-wrap :global(.wx-row:has(.quill-text-cell.is-editing) .wx-cell-box) {
    overflow: visible;
}
.events-table-wrap :global(.quill-text-cell.is-editing) {
    z-index: 30;
}
</style>
